/**
 * Sugestões para o campo identificador do passo URA.
 *
 * O motor (uraService) aceita o passo quando QUALQUER regra abaixo casa:
 * - texto igual à mensagem do cliente (vírgula = sinônimos);
 * - `$variavel` — variável preenchida no contexto;
 * - `!$variavel` — variável vazia, ausente ou "0";
 * - identificador vazio — qualquer resposta (com ressalvas de validação);
 * - match aproximado de intenção (ex.: "comprar passagem" ≈ botão "Comprar passagem").
 *
 * Condições (seção abaixo) refinam com operadores: igual, maior, contém etc.
 */

import {
  parseChatbotButtonEntries,
  partitionChatbotButtonEntries,
} from "./chatbotButtonLinks";
import {
  VARIAVEIS_CONTEXTO,
  contextKeyFromVariableName,
} from "./chatbotContextVariables";

export const IDENT_SUGGESTION_GROUP_LABELS = {
  menu_option: "Resposta do cliente",
  parent_button: "Opção do menu anterior",
  context_filled: "Quando já temos a informação",
  context_empty: "Quando ainda não temos",
};

/** Textos amigáveis por variável (perspectiva de quem monta o fluxo). */
const CONTEXT_IDENTIFIER_COPY = {
  cadastroencontrado: {
    filled: {
      label: "Cliente já cadastrado",
      hint: "Depois que a plataforma encontrar o cadastro",
    },
    empty: {
      label: "Cliente não cadastrado",
      hint: "Quando a plataforma não encontrar o cadastro",
    },
  },
  lgpd: {
    filled: {
      label: "LGPD já aceita",
      hint: "Quando o cadastro da solicitação já tem aceite LGPD",
    },
    empty: {
      label: "LGPD ainda não aceita",
      hint: "Quando o cadastro da solicitação ainda não tem aceite LGPD",
    },
  },
  nomecliente: {
    filled: { label: "Nome do cliente já conhecido", hint: "Quando o atendimento já tem o nome" },
    empty: { label: "Nome ainda não informado", hint: "Antes de pedir o nome ao cliente" },
  },
  cpfcliente: {
    filled: { label: "CPF já informado", hint: "Depois que o cliente informou o CPF" },
    empty: { label: "CPF ainda não informado", hint: "Antes de pedir o CPF" },
  },
  cnpjcliente: {
    filled: { label: "CNPJ já informado", hint: "Depois que o cliente informou o CNPJ" },
    empty: { label: "CNPJ ainda não informado", hint: "Antes de pedir o CNPJ" },
  },
  emailcliente: {
    filled: { label: "E-mail já informado", hint: "Depois que o cliente passou o e-mail" },
    empty: { label: "E-mail ainda não informado", hint: "Antes de pedir o e-mail" },
  },
  telefonecliente: {
    filled: { label: "Telefone já informado", hint: "Depois que o cliente passou o telefone" },
    empty: { label: "Telefone ainda não informado", hint: "Antes de pedir o telefone" },
  },
  celularcliente: {
    filled: { label: "Celular já informado", hint: "Depois que o cliente passou o celular" },
    empty: { label: "Celular ainda não informado", hint: "Antes de pedir o celular" },
  },
  datadigitada: {
    filled: { label: "Data já informada pelo cliente", hint: "Depois que o cliente digitou uma data" },
    empty: { label: "Data ainda não informada", hint: "Antes de pedir a data" },
  },
};

function shortVariableName(label) {
  return String(label || "")
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function contextSuggestionCopy(key, variableLabel, mode) {
  const custom = CONTEXT_IDENTIFIER_COPY[key]?.[mode];
  if (custom) return custom;

  const name = shortVariableName(variableLabel) || key;
  if (mode === "filled") {
    return {
      label: `Já temos: ${truncateLabel(name, 40)}`,
      hint: "Este passo entra depois que essa informação foi obtida",
    };
  }
  return {
    label: `Ainda falta: ${truncateLabel(name, 40)}`,
    hint: "Este passo entra quando essa informação ainda não foi coletada",
  };
}

/** Ordem de destaque no autocomplete (menor = mais acima). */
const CONTEXT_SORT_PRIORITY = {
  cadastroencontrado: 1,
  lgpd: 2,
  cpfcliente: 3,
  cnpjcliente: 4,
  nomecliente: 5,
  nomeclientevalido: 6,
  emailcliente: 7,
  telefonecliente: 8,
  celularcliente: 9,
  datadigitada: 10,
  mensagem: 11,
  resposta: 12,
};

const MAX_PARENT_BUTTON_SUGGESTIONS = 8;

function contextVariablePriority(key, mode) {
  const rank = CONTEXT_SORT_PRIORITY[key] ?? 100;
  const modeOffset = mode === "filled" ? 0 : 1;
  return 30 + rank * 2 + modeOffset;
}

function identParts(identificador) {
  return String(identificador || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function flowButtonsFromSlot(slot) {
  const entries = parseChatbotButtonEntries(slot?.botoes);
  return partitionChatbotButtonEntries(entries)
    .replies.map((e) => e.flowKey)
    .filter(Boolean);
}

function extractMenuOptionFromNome(nome) {
  const n = String(nome || "").trim();
  if (!n.includes(" - ")) return null;
  const part = n.split(" - ")[0].trim();
  return part || null;
}

function shortSlotLabel(slot) {
  const nome = String(slot?.nome || "").trim();
  if (nome) return nome.length > 48 ? `${nome.slice(0, 45)}…` : nome;
  const ident = identParts(slot?.identificador)[0];
  if (ident) return ident.length > 48 ? `${ident.slice(0, 45)}…` : ident;
  return `Passo #${slot?.id}`;
}

function childMatchesButton(child, flowKey) {
  const key = normalizeKey(flowKey);
  return identParts(child.identificador).some((part) => normalizeKey(part) === key);
}

function truncateLabel(text, max = 52) {
  const s = String(text || "").trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function pushSuggestion(bucket, seen, item) {
  const value = String(item?.value || "").trim();
  if (!value) return;
  const key = normalizeKey(value);
  if (!key || seen.has(key)) return;
  seen.add(key);
  bucket.push({
    value,
    label: item.label || value,
    group: item.group,
    hint: item.hint || "",
    priority: Number(item.priority) || 50,
  });
}

function matchesInputFilter(item, inputFilter) {
  const q = normalizeKey(inputFilter);
  if (!q) return true;
  return (
    normalizeKey(item.value).includes(q) ||
    normalizeKey(item.label).includes(q) ||
    normalizeKey(item.hint).includes(q)
  );
}

function buildContextIdentifierSuggestions(seen, { inputFilter = "" } = {}) {
  const suggestions = [];

  for (const variable of VARIAVEIS_CONTEXTO) {
    const key = contextKeyFromVariableName(variable.name);
    if (!key) continue;

    const copyFilled = contextSuggestionCopy(key, variable.label, "filled");
    const copyEmpty = contextSuggestionCopy(key, variable.label, "empty");
    const filled = {
      value: `$${key}`,
      label: copyFilled.label,
      group: "context_filled",
      hint: copyFilled.hint,
      priority: contextVariablePriority(key, "filled"),
    };
    const empty = {
      value: `!$${key}`,
      label: copyEmpty.label,
      group: "context_empty",
      hint: copyEmpty.hint,
      priority: contextVariablePriority(key, "empty"),
    };

    if (!matchesInputFilter(filled, inputFilter) && !matchesInputFilter(empty, inputFilter)) {
      continue;
    }

    pushSuggestion(suggestions, seen, filled);
    pushSuggestion(suggestions, seen, empty);
  }

  return suggestions;
}

/**
 * @param {{ slot?: object, allSlots?: object[], slotNome?: string, selected?: string[], inputFilter?: string }} params
 */
export function buildChatbotIdentifierSuggestions({
  slot,
  allSlots = [],
  slotNome,
  selected = [],
  inputFilter = "",
} = {}) {
  if (!slot) return [];

  const slotList = Array.isArray(allSlots) ? allSlots : [];
  const slotById = new Map(slotList.map((s) => [Number(s.id), s]));
  const selectedKeys = new Set(selected.map((v) => normalizeKey(v)).filter(Boolean));
  const suggestions = [];
  const seen = new Set(selectedKeys);
  const nome = String(slotNome ?? slot.nome ?? "").trim();

  const menuOption = extractMenuOptionFromNome(nome);
  if (menuOption) {
    pushSuggestion(suggestions, seen, {
      value: menuOption,
      label: menuOption,
      group: "menu_option",
      hint: "Quando o cliente escolhe ou digita isso",
      priority: 1,
    });
  }

  const parentId = Number(slot.id_pai) || 0;
  const parent = parentId > 0 ? slotById.get(parentId) : null;
  if (parent) {
    const parentLabel = shortSlotLabel(parent);
    const siblings = slotList.filter(
      (s) =>
        Number(s.id_pai) === parentId &&
        Number(s.id) !== Number(slot.id) &&
        String(s.identificador || "").trim() !== "Exceção"
    );

    let parentButtonCount = 0;
    for (const flowKey of flowButtonsFromSlot(parent)) {
      if (parentButtonCount >= MAX_PARENT_BUTTON_SUGGESTIONS) break;
      const takenBySibling = siblings.find((child) => childMatchesButton(child, flowKey));
      if (takenBySibling) continue;
      pushSuggestion(suggestions, seen, {
        value: flowKey,
        label: flowKey,
        group: "parent_button",
        hint: `Se o cliente escolheu "${flowKey}" em "${parentLabel}"`,
        priority: 10 + parentButtonCount,
      });
      parentButtonCount += 1;
    }
  }

  suggestions.push(...buildContextIdentifierSuggestions(seen, { inputFilter }));

  return suggestions
    .filter((item) => matchesInputFilter(item, inputFilter))
    .sort((a, b) => a.priority - b.priority || a.label.localeCompare(b.label, "pt-BR"));
}
